<template>
    <div class="vendor-component">
        <card title="Vendor Record Maintenance">
            <template #header-right>
                <history-modal v-model="vendor" />
            </template>
            <form>
                <NameSearchComponent v-model="displayName" :named-person="vendor" :searchMode="disableFields"
                    :disable-fields="disableFields" :show-title="false" :show-suffix="false"
                    @person-update="personUpdate" @input="nameChanged">
                    <div style="min-width: 250px;">
                        <fg-input v-model="vendor.phoneNumber" label="Phone" :disabled="disableFields" />
                    </div>
                </NameSearchComponent>

                <b-form-row>
                    <b-col>
                        <AddressFormFields v-model="vendor" :disable-fields="disableFields">
                            <template #row-1>
                                <b-col cols="4" offset="1">
                                    <fg-input v-model="vendor.faxNumber" label="Fax" :disabled="disableFields" />
                                </b-col>
                            </template>
                        </AddressFormFields>
                    </b-col>
                </b-form-row>
                <b-form-row>
                    <b-col cols="7">
                        <fg-input v-model="vendor.emailAddress" label="Email" type="email" :disabled="disableFields" />
                    </b-col>
                </b-form-row>
                <b-row>
                    <b-col>
                        <fg-input label="Note">
                            <textarea v-model="vendor.note" class="form-control textarea-autosize" :disabled="disableFields">
                </textarea>
                        </fg-input>
                    </b-col>
                </b-row>
            </form>

            <hr>
            <h3>Inventory SFTP Settings</h3>
            <b-row>
                <b-col cols="6">
                    <fg-input v-model="vendor.sftpInventoryHost" label="Host" :disabled="disableFields" />
                </b-col>
                <b-col cols="2">
                    <fg-input v-model.number="vendor.sftpPort" type="number" label="Port" :disabled="disableFields" />
                </b-col>
                <b-col cols="4">
                    <fg-input v-model="vendor.sftpBaseDir" label="Base Directory" :disabled="disableFields" />
                </b-col>
                <b-col cols="6">
                    <fg-input v-model="vendor.sftpUsername" label="Username" :disabled="disableFields" />
                </b-col>
                <b-col cols="6">
                    <fg-input v-model="vendor.sftpPassword" label="Password" :disabled="disableFields" />
                </b-col>
            </b-row>
            <b-row>
                <b-col cols="4">
                    <b-form-group label="Receiver Id" :disabled="disableFields">
                        <b-input v-model="vendor.receiverId" />
                    </b-form-group>
                </b-col>
            </b-row>

            <NewClearSaveButtons :titles="buttonTitles" :disable="disableFields" :disable-save="!isFormValid"
                :disable-clear="disableFields" @new-clicked="createVendor" @clear-clicked="undoChanges"
                @save-clicked="saveVendor" />
        </card>
    </div>
</template>

<script lang="ts">
import axios, { AxiosError } from 'axios';
import { Component, Watch, Mixins } from 'vue-property-decorator';
import DisableAutocompleteMixin from '@/mixins/DisableAutocompleteMixin';
import { LockHandler, Lock } from '@/mixins/LockHandler';
import { NotificationOptions } from "@/util/NotificationOptionsPresets";
import { Vendor } from '@/models/Vendor';

import NameSearchComponent from '@/components/NameSearchComponent.vue';
import NewClearSaveButtons from '@/components/NewClearSaveButtons.vue';
import AddressFormFields from '@/components/AddressFormFields.vue';
import HistoryModal from "@/components/Modals/HistoryModal.vue";
import moment from "moment";
import { EntityType } from '@/models/EntityType';

@Component({
    name: 'VendorDetailsPage',
    components: { HistoryModal, NameSearchComponent, NewClearSaveButtons, AddressFormFields }
})
export default class VendorDetailsPage extends Mixins(LockHandler, DisableAutocompleteMixin) {
    private vendor: Vendor = new Vendor();
    private vendorLock: Lock | null = null;

    private buttonTitles = {
        save: "Save Changes",
        new: "New Vendor",
        clear: "Clear",
    };

    private newMode = false;
    private nameIntroduced = "";
    private displayName = "";

    get disableFields(): boolean {
        const lockedForUser = !this.vendorLock;
        const noEntityLoaded = !(this.vendor && this.vendor.id);

        // Disable fields if
        // ** its locked for the user
        // ** or if there is no doctor loaded and is not in New object Mode.
        return (lockedForUser || noEntityLoaded) && !this.newMode;
    }

    @Watch('vendor') onVendorChanged(value: Vendor, oldValue: Vendor) {
        if (!value) {
            this.displayName = "";
            return;
        }

        if (value && value.id && (!oldValue || oldValue.id != value.id)) {
            this.addLockWithURL(`/Vendor/${value.id}/lock`, 60000)
                .then(result => {
                    this.vendorLock = result;
                })
                .catch(error => {
                    if (error.response && error.response.status == 418) {
                        // vendor locked
                        console.log('Doctor is locked');
                        console.log(error.response);
                        this.showLockedMessage(
                            EntityType.Vendor,
                            value.id.toString(),
                            value.name,
                            error.response.data
                        );
                    }
                })
                .finally(() => {
                    axios.get(`/Vendor/${value.id}`)
                        .then(response => {
                            this.vendor = Object.assign(new Vendor(), response.data);
                        })
                        .catch(err => {
                            const error = err as AxiosError;
                            console.warn(error);
                            if (error.response?.status === 418) {
                                this.showLockedMessage(
                                    EntityType.Vendor,
                                    value?.id?.toString(),
                                    value?.name,
                                    error.response.data
                                );
                            } else {
                                this.$notification(NotificationOptions.error(error));
                            }
                        });
                });

        }

        if (value && value.name) {
            this.displayName = value.name;
        }
    }

    nameChanged(val: string) {
        this.nameIntroduced = val;
    }

    get isFormValid() {
        return !!(this.nameIntroduced);
    }

    personUpdate(value: any) {
        this.vendor = value;
        this.nameIntroduced = this.vendor.displayNamesForPerson();
    }

    async undoChanges() {
        try {
            const value = await this.$bvModal.msgBoxConfirm('Are you sure you want to clear?', {
                title: 'Confirm',
                okVariant: 'danger',
                centered: true,
                noFade: true
            });

            if (!value) return;

            if (this.vendorLock && this.vendorLock.refreshURL) {
                this.releaseLockAtURL(this.vendorLock.refreshURL);
                this.vendorLock = null;
            }
            this.vendor = new Vendor();
            this.newMode = false;
            this.displayName = "";
            this.nameIntroduced = "";
        } catch (err) {
            console.error("Error caught in undoChanges()", err);
        }
    }

    createVendor() {
        this.vendor = new Vendor();
        this.newMode = true;
    }

    async saveVendor() {
        try {
            const vendor: Vendor = this.vendor;
            vendor.name = this.displayName;

            const response = await axios.post<Vendor>('/Vendor', vendor);
            this.vendor = new Vendor(response.data);
            this.$notification(NotificationOptions.successSaveNotificationPreset('Vendor'));
        } catch (err) {
            const error = err as AxiosError;
            console.error(error);
            if (error.response?.status === 418) {
                this.showLockedMessage(
                    EntityType.Vendor,
                    this.vendor?.id?.toString(),
                    this.vendor?.name,
                    error.response.data
                );
            } else {
                this.$notification(NotificationOptions.errorSaveNotificationPreset('Vendor', error));
            }
        }
    }

} // close export of class

</script>